import { createRouter, createRootRoute, createRoute, Outlet, redirect, notFound } from '@tanstack/react-router';
import { LoginPage } from '@/pages/auth/login';
import { SignupPage } from '@/pages/auth/signup';
import { VerifyEmailPage } from '@/pages/auth/verify-email';
import { ForgotPasswordPage } from '@/pages/auth/forgot-password';
import { ResetPasswordPage } from '@/pages/auth/reset-password';
import { DashboardPage } from '@/pages/dashboard';
import { DatabasesPage } from '@/pages/databases';
import { QueryPage } from '@/pages/query/[id]';
import { QueryEditorPage } from '@/pages/query';
import { HistoryPage } from '@/pages/history';
import { SavedQueriesPage } from '@/pages/saved-queries';
import { DataBrowserPage } from '@/pages/data-browser';
import { VisualQueryBuilderPage } from '@/pages/visual-query-builder';
import { VisualSchemaDesignerPage } from '@/pages/visual-schema-designer';
import { AnalyticsPage } from '@/pages/analytics';
import { SettingsPage } from '@/pages/settings';
import { TeamPage } from '@/pages/team';
import { SecurityPage } from '@/pages/security';
import { CollaborationDemoPage } from '@/pages/collaboration-demo';
import { CollaborationPage } from '@/pages/collaboration';
import { HelpPage } from '@/pages/help';
import { TemplatesExplorePage } from '@/pages/templates/explore';
import { NotFoundPage } from '@/pages/404';
import { ProtectedRoute } from '@/components/auth/protected-route';
import { CommandBar } from '@/components/command-bar';
import { OnboardingDialog } from '@/components/onboarding-dialog';
import { authClient } from '@/lib/auth-client';

const rootRoute = createRootRoute({
  component: () => (
    <>
      <Outlet />
      <CommandBar />
      <OnboardingDialog />
    </>
  ),
  notFoundComponent: NotFoundPage,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: async () => {
    const session = await authClient.getSession();
    if (session.data) {
      throw redirect({ to: '/dashboard' });
    }
    throw redirect({ to: '/login' });
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
  beforeLoad: async () => {
    const session = await authClient.getSession();
    if (session.data) {
      throw redirect({ to: '/dashboard' });
    }
  },
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  component: SignupPage,
  beforeLoad: async () => {
    const session = await authClient.getSession();
    if (session.data) {
      throw redirect({ to: '/dashboard' });
    }
  },
});

const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify-email',
  component: VerifyEmailPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
  beforeLoad: async () => {
    const session = await authClient.getSession();
    if (session.data) {
      throw redirect({ to: '/dashboard' });
    }
  },
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPasswordPage,
  beforeLoad: async () => {
    const session = await authClient.getSession();
    if (session.data) {
      throw redirect({ to: '/dashboard' });
    }
  },
});

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard',
  component: () => (
    <ProtectedRoute>
      <DashboardPage />
    </ProtectedRoute>
  ),
});

const databasesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/databases',
  component: () => (
    <ProtectedRoute>
      <DatabasesPage />
    </ProtectedRoute>
  ),
});

const queryIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/query',
  component: () => (
    <ProtectedRoute>
      <QueryEditorPage />
    </ProtectedRoute>
  ),
});

const queryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/query/$id',
  component: () => (
    <ProtectedRoute>
      <QueryPage />
    </ProtectedRoute>
  ),
});

const historyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/history',
  component: () => (
    <ProtectedRoute>
      <HistoryPage />
    </ProtectedRoute>
  ),
});

const savedQueriesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/saved-queries',
  component: () => (
    <ProtectedRoute>
      <SavedQueriesPage />
    </ProtectedRoute>
  ),
});

const dataBrowserRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/data-browser',
  component: () => (
    <ProtectedRoute>
      <DataBrowserPage />
    </ProtectedRoute>
  ),
});

const dataBrowserWithIdRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/data-browser/$id',
  component: () => (
    <ProtectedRoute>
      <DataBrowserPage />
    </ProtectedRoute>
  ),
});

const visualQueryBuilderIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/visual-query-builder',
  beforeLoad: () => {
    throw redirect({ to: '/databases' });
  },
});

const visualQueryBuilderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/visual-query-builder/$id',
  component: () => (
    <ProtectedRoute>
      <VisualQueryBuilderPage />
    </ProtectedRoute>
  ),
});

const analyticsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/analytics',
  component: () => (
    <ProtectedRoute>
      <AnalyticsPage />
    </ProtectedRoute>
  ),
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: () => (
    <ProtectedRoute>
      <SettingsPage />
    </ProtectedRoute>
  ),
});

const teamRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/team',
  component: () => (
    <ProtectedRoute>
      <TeamPage />
    </ProtectedRoute>
  ),
});

const securityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/security',
  component: () => (
    <ProtectedRoute>
      <SecurityPage />
    </ProtectedRoute>
  ),
});

const collaborationDemoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/collaboration-demo',
  component: CollaborationDemoPage,
});

const collaborationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/collaboration',
  component: () => (
    <ProtectedRoute>
      <CollaborationPage />
    </ProtectedRoute>
  ),
});

const visualSchemaDesignerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/visual-schema-designer',
  component: () => (
    <ProtectedRoute>
      <VisualSchemaDesignerPage />
    </ProtectedRoute>
  ),
});

const helpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/help',
  component: () => (
    <ProtectedRoute>
      <HelpPage />
    </ProtectedRoute>
  ),
});

const templatesExploreRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/templates/explore',
  component: () => (
    <ProtectedRoute>
      <TemplatesExplorePage />
    </ProtectedRoute>
  ),
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  signupRoute,
  verifyEmailRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  dashboardRoute,
  databasesRoute,
  queryIndexRoute,
  queryRoute,
  historyRoute,
  savedQueriesRoute,
  dataBrowserRoute,
  dataBrowserWithIdRoute,
  visualQueryBuilderIndexRoute,
  visualQueryBuilderRoute,
  visualSchemaDesignerRoute,
  analyticsRoute,
  settingsRoute,
  teamRoute,
  securityRoute,
  collaborationDemoRoute,
  collaborationRoute,
  helpRoute,
  templatesExploreRoute,
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
